import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationFacade } from '../../notifications';
import { AccountTicketsService } from '../services/account-tickets.service';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Ticket } from '../models/ticket.model';
import { of } from 'rxjs';

import * as ACTIONS from './account-tickets.actions';
import { Store } from '@ngrx/store';
import { getPage } from './account-tickets.selectors';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AccountTicketsEffects {
    constructor(
        private actions$: Actions,
        private notification: NotificationFacade,
        private service: AccountTicketsService,
        private store: Store,
        private translate: TranslateService
    ) {}

    fetchTickets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.fetchAllAccountTickets),
            mergeMap((payload: { expiredHours?: number; expiredMinutes?: number }) =>
                this.service
                    .fetchAccountTickets({
                        expiredHours: payload.expiredHours,
                        expiredMinutes: payload.expiredMinutes
                    })
                    .pipe(
                        map((tickets: Ticket[] = []) => {
                            return ACTIONS.fetchAllAccountTicketsSuccess({ tickets });
                        }),
                        catchError(() => {
                            this.notification.error(this.translate.instant('MY_TICKET_LIST_ERR'));
                            return of(ACTIONS.fetchAllAccountTicketsFailed());
                        })
                    )
            )
        );
    });

    fetchPaginatedTickets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.fetchPaginatedAccountTickets),
            mergeMap((payload: { limit: number; page: number; expiredHours?: number; expiredMinutes?: number }) =>
                this.service
                    .fetchAccountTickets({
                        limit: payload.limit,
                        page: payload.page,
                        expiredHours: payload.expiredHours,
                        expiredMinutes: payload.expiredMinutes
                    })
                    .pipe(
                        map((tickets: Ticket[] = []) => {
                            return ACTIONS.fetchPaginatedAccountTicketsSuccess({
                                tickets,
                                page: payload.page,
                                limit: payload.limit
                            });
                        }),
                        catchError(() => {
                            this.notification.error(this.translate.instant('MY_TICKET_LIST_ERR'));
                            return of(ACTIONS.fetchPaginatedAccountTicketsFailed());
                        })
                    )
            )
        );
    });

    refetchAllAccountTicketPages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.refetchAllAccountTicketPages),
            withLatestFrom(this.store.select(getPage)),
            mergeMap(([action, currentPage]) =>
                this.service
                    .fetchAccountTickets({
                        limit: action.limit * (currentPage + 1),
                        page: 0,
                        expiredHours: action.expiredHours,
                        expiredMinutes: action.expiredMinutes
                    })
                    .pipe(
                        map((tickets: Ticket[] = []) => {
                            return ACTIONS.refetchAllAccountTicketPagesSuccess({ tickets });
                        }),
                        catchError(() => {
                            this.notification.error(this.translate.instant('MY_TICKET_LIST_ERR'));
                            return of(ACTIONS.refetchAllAccountTicketPagesFailed());
                        })
                    )
            )
        );
    });

    fetchDeviceTickets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.fetchDeviceTickets),
            mergeMap((payload: { expiredHours?: number; expiredMinutes?: number }) => {
                return this.service
                    .fetchAccountTickets(
                        { expiredHours: payload.expiredHours, expiredMinutes: payload.expiredMinutes },
                        true
                    )
                    .pipe(
                        map((tickets: Ticket[]) => {
                            return ACTIONS.fetchDeviceTicketsSuccess({ tickets });
                        }),
                        catchError(() => {
                            this.notification.error(this.translate.instant('MY_TICKET_LIST_ERR'));
                            return of(ACTIONS.fetchDeviceTicketsFailed());
                        })
                    );
            })
        );
    });

    fetchTicketsHistory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.fetchAccountTicketsHistory),
            mergeMap((payload: { formattedFromDate; formattedToDate; includeReturnedTickets }) =>
                this.service
                    .fetchAccountTicketsHistory(
                        payload.formattedFromDate,
                        payload.formattedToDate,
                        payload.includeReturnedTickets
                    )
                    .pipe(
                        map((tickets: Ticket[]) => {
                            return ACTIONS.fetchAccountTicketsHistorySuccess({ tickets });
                        }),
                        catchError(() => {
                            this.notification.error(this.translate.instant('MY_TICKET_LIST_ERR'));
                            return of(ACTIONS.fetchAccountTicketsHistoryFailed());
                        })
                    )
            )
        );
    });

    activateTicket$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.activateTicket),
            mergeMap((payload: { docNum?: string; id?: number }) =>
                this.service.activateTicket(String(payload.docNum ?? payload.id)).pipe(
                    map((ticket: Ticket) => {
                        this.notification.success(this.translate.instant('TICKET_MANUAL_ACTIVATION_SUCCESS'));
                        return ACTIONS.activateTicketSuccess({ ticket });
                    }),
                    catchError(() => {
                        this.notification.error(this.translate.instant('TICKET_MANUAL_ACTIVATION_ERROR'));
                        return of(ACTIONS.activateTicketFailed());
                    })
                )
            )
        );
    });

    activateTicketSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.activateTicketSuccess),
            mergeMap(() => {
                return this.service.fetchAccountTickets({ expiredHours: 0, expiredMinutes: 0 }).pipe(
                    map((tickets: Ticket[]) => {
                        return ACTIONS.refetchAllAccountTicketPagesSuccess({ tickets });
                    }),
                    catchError(() => {
                        this.notification.error(this.translate.instant('MY_TICKET_LIST_ERR'));
                        return of(ACTIONS.refetchAllAccountTicketPagesFailed());
                    })
                );
            })
        );
    });

    reactivateTicket$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ACTIONS.reactivateTicket),
            mergeMap((payload: { docNum?: string; id?: number }) =>
                this.service.activateTicket(String(payload.docNum ?? payload.id)).pipe(
                    map((data) => {
                        const payload = data as { id: number; status: string; startDate: Date; endDate: Date };
                        this.notification.success(this.translate.instant('TICKET_MANUAL_ACTIVATION_SUCCESS'));
                        return ACTIONS.reactivateTicketSuccess({
                            id: payload.id,
                            status: payload.status,
                            startDate: payload.startDate,
                            endDate: payload.endDate
                        });
                    }),
                    catchError(() => {
                        this.notification.error(this.translate.instant('TICKET_MANUAL_ACTIVATION_ERROR'));
                        return of(ACTIONS.activateTicketFailed());
                    })
                )
            )
        );
    });
}
